import { useEffect, useMemo, useState } from "react";

import type { EventsPayload } from "../lib/types/demo";


interface EventCatalogProps {
  open: boolean;
  onClose: () => void;
  events?: EventsPayload;
  fleetLabel: string;
  /** Pre-filled search (e.g. an event code from the Site panel). */
  initialQuery?: string;
}

// Physical-topology order for the location groups — head-end down to
// per-seat peripherals, mirroring the LOPA layout (from the original
// generator's CATALOG_LOC_ORDER). Unknown locations sort alphabetically
// at the bottom.
const LOC_ORDER = [
  "File Server", "Content Server",
  "Aircraft Interface", "Aircraft Inputs",
  "Master Control Unit MCU", "Network Controller NC",
  "Area Distribution Box ADB", "Seat Interface Box SIB", "ZTDC",
  "Seat Electronics Box SEB", "Eco DC SEB", "High Power Seat Box HPS",
  "First Class SDU", "Economy SDU",
  "Seat Power Module SPM", "Handset",
  "Crew Terminal",
  "System Reboot", "LRU Reboot",
];
const locRank = (loc: string): number => {
  const i = LOC_ORDER.indexOf(loc);
  return i === -1 ? LOC_ORDER.length : i;
};


// Fleet-specific event catalog browser: every fault event applicable to THIS
// fleet (appendix-filtered upstream — narrowbodies ~151, eX3 widebodies ~226),
// searchable, filterable by category, grouped by location layer.
export default function EventCatalog({
  open, onClose, events, fleetLabel, initialQuery,
}: EventCatalogProps) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>("All");
  const [scoringOnly, setScoringOnly] = useState(false);
  // Location groups the user opened. Collapsed by default — the header's
  // count + scoring/info split says where the volume lives without
  // expanding anything. A search or filter force-opens matching groups.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setQuery(initialQuery ?? ""); setCategory("All"); setScoringOnly(false);
      setExpanded(new Set());
    }
  }, [open, initialQuery]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const catalog = events?.event_catalog ?? {};

  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const e of Object.values(catalog)) set.add(e.category || "Other");
    return ["All", ...[...set].sort()];
  }, [catalog]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return Object.entries(catalog)
      .filter(([code, e]) => {
        if (category !== "All" && (e.category || "Other") !== category) return false;
        if (scoringOnly && e.score !== "Y") return false;
        if (!q) return true;
        return (
          code.toLowerCase().includes(q) ||
          e.name.toLowerCase().includes(q) ||
          e.scope.toLowerCase().includes(q) ||
          e.location.toLowerCase().includes(q) ||
          e.type.toLowerCase().includes(q)
        );
      })
      .sort(([a], [b]) => a.localeCompare(b));
  }, [catalog, query, category, scoringOnly]);

  // Group by location layer, ordered head-end → seat peripherals.
  const grouped = useMemo(() => {
    const g = new Map<string, typeof rows>();
    for (const r of rows) {
      const loc = r[1].location || "Other";
      if (!g.has(loc)) g.set(loc, []);
      g.get(loc)!.push(r);
    }
    return [...g.entries()].sort(
      (a, b) => locRank(a[0]) - locRank(b[0]) || a[0].localeCompare(b[0]),
    );
  }, [rows]);

  // Any active search/filter force-opens every matching group — nobody
  // wants to click 18 chevrons after typing a query.
  const filterActive = query.trim() !== "" || category !== "All" || scoringOnly;
  const isOpen = (loc: string) => filterActive || expanded.has(loc);
  const allOpen = filterActive ||
    (grouped.length > 0 && grouped.every(([loc]) => expanded.has(loc)));
  const toggleGroup = (loc: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(loc)) next.delete(loc);
      else next.add(loc);
      return next;
    });
  };
  const toggleAll = () => {
    setExpanded(allOpen ? new Set() : new Set(grouped.map(([loc]) => loc)));
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[90] bg-slate-900/45 backdrop-blur-[2px]"
      onMouseDown={onClose}
    >
      <div
        className="mx-auto mt-[6vh] flex h-[84vh] w-[860px] max-w-[94vw] flex-col overflow-hidden rounded-2xl bg-white shadow-[0_24px_80px_rgba(15,23,42,0.45)] ring-1 ring-slate-900/10"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-slate-100 px-6 pb-3 pt-4">
          <div className="flex items-baseline justify-between">
            <h2 className="text-base font-bold tracking-tight text-slate-900">
              Event catalog
              <span className="ml-2 text-xs font-semibold text-slate-400">
                {fleetLabel} · {Object.keys(catalog).length} applicable events
              </span>
            </h2>
            <button
              onClick={onClose}
              className="rounded-full px-2 py-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
            >
              ✕
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search code, name, scope, location…"
              className="w-64 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-[12.5px] text-slate-800 placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:outline-none"
            />
            <div className="flex gap-1 overflow-x-auto">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  className={
                    "shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors " +
                    (category === c
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200")
                  }
                >
                  {c}
                </button>
              ))}
            </div>
            <label className="ml-auto flex shrink-0 cursor-pointer items-center gap-1.5 text-[11px] font-semibold text-slate-600">
              <input
                type="checkbox"
                checked={scoringOnly}
                onChange={(e) => setScoringOnly(e.target.checked)}
                className="accent-red-500"
              />
              scoring only
            </label>
            <button
              onClick={toggleAll}
              disabled={filterActive}
              title={filterActive
                ? "Groups auto-expand while a search or filter is active"
                : undefined}
              className="shrink-0 text-[11px] font-semibold text-sky-600 hover:text-sky-800 disabled:cursor-default disabled:text-slate-300"
            >
              {allOpen ? "Collapse all" : "Expand all"}
            </button>
          </div>
        </div>

        {/* Rows */}
        <div className="flex-1 overflow-y-auto px-6 py-3">
          {rows.length === 0 && (
            <p className="py-10 text-center text-sm text-slate-400">
              No events match.
            </p>
          )}
          {grouped.map(([loc, list]) => {
            const nScoring = list.filter(([, e]) => e.score === "Y").length;
            const nInfo = list.length - nScoring;
            const openGroup = isOpen(loc);
            return (
            <section key={loc} className="mb-1.5">
              <button
                onClick={() => toggleGroup(loc)}
                disabled={filterActive}
                className={
                  "sticky top-0 z-10 flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left backdrop-blur transition-colors " +
                  (openGroup ? "bg-white/95" : "bg-slate-50/95 hover:bg-slate-100") +
                  (filterActive ? " cursor-default" : "")
                }
              >
                <span className={
                  "text-[9px] text-slate-400 transition-transform " +
                  (openGroup ? "rotate-90" : "")
                }>
                  ▶
                </span>
                <span className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-500">
                  {loc}
                </span>
                <span className="text-[10.5px] font-semibold tabular-nums text-slate-400">
                  · {list.length}
                </span>
                {/* Fixed two-column badge block so the scoring / info
                    counts align vertically across every group row. */}
                <span className="ml-auto grid shrink-0 grid-cols-[74px_56px] items-center gap-1">
                  <span className="text-right">
                    {nScoring > 0 ? (
                      <span className="inline-block rounded-full bg-red-100 px-1.5 py-0.5 text-[9px] font-bold tabular-nums text-red-600">
                        {nScoring} scoring
                      </span>
                    ) : (
                      <span className="pr-2 text-[9px] text-slate-300">—</span>
                    )}
                  </span>
                  <span className="text-right">
                    {nInfo > 0 ? (
                      <span className="inline-block rounded-full bg-slate-200/80 px-1.5 py-0.5 text-[9px] font-semibold tabular-nums text-slate-500">
                        {nInfo} info
                      </span>
                    ) : (
                      <span className="pr-2 text-[9px] text-slate-300">—</span>
                    )}
                  </span>
                </span>
              </button>
              {openGroup && (
              <div className="mt-1 space-y-1 pb-2">
                {list.map(([code, e]) => (
                  <div
                    key={code}
                    className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-1.5"
                  >
                    <p className="flex items-center gap-2">
                      <span className="font-mono text-[11.5px] font-bold text-slate-800">
                        {code}
                      </span>
                      {e.name !== code && (
                        <span className="truncate text-[11.5px] text-slate-600">{e.name}</span>
                      )}
                      <span
                        className={
                          "ml-auto shrink-0 rounded-full px-2 py-0.5 text-[9.5px] font-bold " +
                          (e.score === "Y"
                            ? "bg-red-100 text-red-600"
                            : "bg-slate-200 text-slate-500")
                        }
                      >
                        {e.score === "Y" ? "SCORING" : "INFO"}
                      </span>
                      <span className="shrink-0 rounded-full bg-slate-200/70 px-2 py-0.5 text-[9.5px] font-semibold text-slate-500">
                        {e.category}
                      </span>
                    </p>
                    {(e.scope !== "—" || e.penalty !== "None") && (
                      <p className="mt-0.5 text-[10.5px] leading-relaxed text-slate-500">
                        {e.scope !== "—" && e.scope}
                        {e.penalty !== "None" && (
                          <span className="ml-2 font-semibold text-slate-600">
                            penalty: {e.penalty}
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                ))}
              </div>
              )}
            </section>
            );
          })}
        </div>

        <div className="border-t border-slate-100 bg-slate-50/60 px-6 py-2 text-[10.5px] text-slate-400">
          {rows.length} shown · catalog is filtered to events applicable to
          this fleet's configuration
        </div>
      </div>
    </div>
  );
}
